class RDStationRecaptcha {
    constructor() {
        this.siteKey = document.querySelector('meta[name="rv3:sitekey"]')?.content;
        this.verifyUrl = document.querySelector('meta[name="recaptcha:verify-url"]')?.content;
        this.origin = document.querySelector('meta[name="recaptcha:origin"]')?.content || 'https://www.google.com/recaptcha';
        this.locale = document.querySelector('meta[name="locale"]')?.content || 'pt-BR';
        this.errorMessage =
            document.querySelector('meta[name="recaptcha:error-message"]')?.content ||
            'Não foi possível validar sua solicitação. Tente novamente.';
        this.formStates = new WeakMap();
        this.allowedSubmissions = new WeakSet();
        this.recaptchaLoader = null;

        if (!this.siteKey || !this.verifyUrl) return;

        document.addEventListener('focusin', event => this.handleFieldFocus(event));
        document.addEventListener('submit', event => this.handleSubmit(event), true);
    }

    isRDStationForm(form) {
        return (
            form instanceof HTMLFormElement &&
            form.matches('form[id^="conversion-form-"][action*="rdstation.com"]')
        );
    }

    handleFieldFocus(event) {
        const field = event.target;
        if (
            !field.matches(
                'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select'
            )
        ) {
            return;
        }

        const form = field.closest('form');
        if (!this.isRDStationForm(form)) return;

        this.prepareWidget(form, this.getFormState(form)).catch(() => {});
    }

    handleSubmit(event) {
        const form = event.target;
        if (!this.isRDStationForm(form)) return;

        if (this.allowedSubmissions.has(form)) {
            this.allowedSubmissions.delete(form);
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const state = this.getFormState(form);
        if (state.verifying) return;

        state.verifying = true;
        state.submitter = event.submitter || null;
        this.setBusy(form, true);
        this.clearError(form);

        this.execute(form, state).catch(() => {
            state.verifying = false;
            this.setBusy(form, false);
            this.showError(form);
        });
    }

    getFormState(form) {
        if (!this.formStates.has(form)) {
            this.formStates.set(form, {
                submitter: null,
                verifying: false,
                widgetId: null,
                widgetPromise: null,
            });
        }

        return this.formStates.get(form);
    }

    async execute(form, state) {
        await this.prepareWidget(form, state);
        grecaptcha.execute(state.widgetId);
    }

    async prepareWidget(form, state) {
        if (state.widgetId !== null) return;
        if (state.widgetPromise) return state.widgetPromise;

        state.widgetPromise = this.createWidget(form, state);

        try {
            await state.widgetPromise;
        } finally {
            state.widgetPromise = null;
        }
    }

    async createWidget(form, state) {
        await this.loadRecaptcha();

        if (state.widgetId === null) {
            const container = document.createElement('div');
            container.className = 'rd-recaptcha-widget';
            document.body.appendChild(container);

            state.widgetId = grecaptcha.render(container, {
                sitekey: this.siteKey,
                size: 'invisible',
                badge: 'bottomright',
                callback: token => this.verify(form, state, token),
                'error-callback': () => this.fail(form, state),
                'expired-callback': () => this.fail(form, state),
            });
        }
    }

    loadRecaptcha() {
        if (this.recaptchaLoader) return this.recaptchaLoader;

        this.recaptchaLoader = new Promise((resolve, reject) => {
            const ready = () => {
                if (!window.grecaptcha) {
                    reject(new Error('Google reCAPTCHA is unavailable.'));
                    return;
                }

                grecaptcha.ready(resolve);
            };

            if (window.grecaptcha) {
                ready();
                return;
            }

            const existingScript = document.querySelector('script[src*="/recaptcha/api.js"]');
            if (existingScript) {
                existingScript.addEventListener('load', ready, { once: true });
                existingScript.addEventListener('error', reject, { once: true });
                return;
            }

            const callbackName = 'rdStationRecaptchaLoaded';
            const script = document.createElement('script');
            const language = encodeURIComponent(this.locale.replace('_', '-'));

            window[callbackName] = () => {
                delete window[callbackName];
                ready();
            };

            script.async = true;
            script.defer = true;
            script.src = `${this.origin}/api.js?onload=${callbackName}&render=explicit&hl=${language}`;
            script.addEventListener('error', reject, { once: true });
            document.head.appendChild(script);
        });

        return this.recaptchaLoader;
    }

    async verify(form, state, token) {
        try {
            const response = await fetch(this.verifyUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                },
                body: JSON.stringify({ 'g-recaptcha-response': token }),
            });

            if (!response.ok) throw new Error('Google reCAPTCHA validation failed.');

            state.verifying = false;
            this.setBusy(form, false);
            this.allowedSubmissions.add(form);

            if (state.submitter && state.submitter.form === form) {
                form.requestSubmit(state.submitter);
            } else {
                form.requestSubmit();
            }

            grecaptcha.reset(state.widgetId);
        } catch (error) {
            this.fail(form, state);
        }
    }

    fail(form, state) {
        state.verifying = false;
        this.setBusy(form, false);
        this.showError(form);

        if (state.widgetId !== null && window.grecaptcha) {
            grecaptcha.reset(state.widgetId);
        }
    }

    setBusy(form, busy) {
        form.setAttribute('aria-busy', busy ? 'true' : 'false');
        form.querySelectorAll('[type="submit"]').forEach(button => {
            if (busy) {
                button.dataset.recaptchaWasDisabled = button.disabled ? 'true' : 'false';
                button.disabled = true;
                return;
            }

            button.disabled = button.dataset.recaptchaWasDisabled === 'true';
            delete button.dataset.recaptchaWasDisabled;
        });
    }

    clearError(form) {
        form.querySelector('.rd-recaptcha-error')?.remove();
    }

    showError(form) {
        this.clearError(form);

        const error = document.createElement('label');
        const submit = form.querySelector('.bricks-form__submit');
        error.className = 'error rd-recaptcha-error';
        error.setAttribute('role', 'alert');
        error.textContent = this.errorMessage;

        if (submit) {
            submit.parentNode.insertBefore(error, submit);
        } else {
            form.appendChild(error);
        }
    }
}

new RDStationRecaptcha();
